import { useSetRecoilState } from "recoil"
import {
  watchPartyScreenLinkSelector,
  watchPartyScreenSelector,
  watchPartyHostIdSelector
} from "../recoil/atoms/watchPartyScreen"
import { usePictureInPicture } from "../hooks/usePictureInPicture"
import {
  playerPropsSelector,
  currentTimeMNPSelector
} from "../recoil/atoms/player"
import { WatchPartyNotification } from "./types"
import { shoppableMNPVideoAtom } from "../recoil/atoms/shoppableVideos"
import { DomError, ElementId } from "../constants/enums"
import { useCallback } from "react"
import { ConsoleLogger } from "../util/logger"
import { isBigPlayIconState } from "../recoil/atoms/bigPlayIcon"
const MeetOnline = window.MeetOnline

const logger = new ConsoleLogger()


interface useWatchPartyEventsCBProps {
  onStopped: (resp: WatchPartyNotification) => void
  onStarted: (resp: WatchPartyNotification) => void
  onMuteChanged: (value: boolean) => void
  onError: (error: Error) => void
}

export const useWatchPartyEventsCB = (): useWatchPartyEventsCBProps => {
  const { exitPictureInPicture } = usePictureInPicture()
  const setWatchPartyScreenLink = useSetRecoilState(
    watchPartyScreenLinkSelector
  )
  const setShoppableMNPVideo = useSetRecoilState(shoppableMNPVideoAtom)
  const setWatchPartyScreen = useSetRecoilState(watchPartyScreenSelector)
  const setWatchPartyHostId = useSetRecoilState(watchPartyHostIdSelector)
  const setPlayerProps = useSetRecoilState(playerPropsSelector)
  const setBigPlayIcon = useSetRecoilState(isBigPlayIconState)
  const setCurrentTimeMNPProps = useSetRecoilState(currentTimeMNPSelector)

  const stopServerWatchParty = async () => {
    setWatchPartyScreen(false)
    setWatchPartyScreenLink("")
    setWatchPartyHostId("")
    exitPictureInPicture()
    setPlayerProps({ isControlVolume: false })
    setShoppableMNPVideo({
      idShoppableMNP: "",
      enabledShoppableMNP: false,
      srcShoppableMNP: "",
      typeShoppableMNP: ""
    })
    await MeetOnline?.client().rtc?.stopWatchParty()
  }

  const handleWatchPartyStopped = (resp: WatchPartyNotification) => {
    setWatchPartyScreen(resp.enabled)
    setWatchPartyScreenLink("")
    setWatchPartyHostId("")
    exitPictureInPicture()
    setPlayerProps({ isControlVolume: false })
  }

  const handleWatchPartyStarted = (resp: WatchPartyNotification) => {
    let wpHostId = MeetOnline?.client().rtc?.getWatchPartyHostId()
    if (
      resp.oldSrc !== "" &&
      (wpHostId === "modNotPresentImage" ||
        wpHostId === "modNotPresentVideo" ||
        wpHostId === "modNotPresentOverlay") &&
      wpHostId !== resp.wpHostId
    ) {
      stopServerWatchParty()
    }

    setWatchPartyHostId(resp.wpHostId)
    setWatchPartyScreen(resp.enabled)
    setWatchPartyScreenLink(resp.src)
    if (resp.wpHostId === "modNotPresentVideo") {
      setCurrentTimeMNPProps({ type: "SAVE_LAST_TIME" })
      setPlayerProps({
        isMNPLastTime: true
      })
    }
  }

  /** This method will be call to change watch party mute state */
  const onMuteChanged = useCallback((value: boolean) => {
      const videoElement = document.getElementById(ElementId.WATCH_PARTY_VIDEO) as HTMLVideoElement
      videoElement.muted = value
  }, [])

  /** This method will be call from the sdk, whenever watch party fails */
  const handleOnError = useCallback((error: Error) => {
    if (error.name === DomError.NOT_ALLOWED_ERROR) {
      // Show big play button so that user can click an play the video
      setBigPlayIcon(true)
    } else {
      // Handle other errors
      logger.error('Error while handling watch party:', error.message);
    }
  }, [])

  return {
    onStopped: handleWatchPartyStopped,
    onStarted: handleWatchPartyStarted,
    onMuteChanged,
    onError: handleOnError
  }
}
